import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useMockData } from '../data/useMockData';

const categoryColors = {
  'Architecture': 'bg-[#00FFD1]/10 text-[#00FFD1]',
  'DevOps': 'bg-purple-500/10 text-purple-400',
  'Cloud': 'bg-blue-500/10 text-blue-400',
  'Backend': 'bg-orange-500/10 text-orange-400',
  'الهندسة المعمارية': 'bg-[#00FFD1]/10 text-[#00FFD1]',
  'السحابة': 'bg-blue-500/10 text-blue-400',
  'الواجهة الخلفية': 'bg-orange-500/10 text-orange-400',
};

const BlogPage = () => {
  const { t } = useTranslation();
  const { blogPosts } = useMockData();
  
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative">
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-semibold text-white mb-6">
              {t('blog.pageTitle')}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              {t('blog.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* Featured Post */}
          <div className="mb-16">
            <div className="p-10 bg-white/[0.02] border border-white/10 hover:border-[#00FFD1]/30 transition-all duration-400 group">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <span className={`inline-block px-4 py-2 text-sm font-medium mb-6 ${categoryColors[blogPosts[0].category]}`}>
                    {blogPosts[0].category}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 group-hover:text-[#00FFD1] transition-colors duration-300">
                    {blogPosts[0].title}
                  </h2>
                  <p className="text-lg text-white/60 mb-6 leading-relaxed">
                    {blogPosts[0].summary}
                  </p>
                  <div className="flex items-center gap-6 text-white/70 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{blogPosts[0].readTime} {t('blog.readTime')}</span>
                    </div>
                    <span>{new Date(blogPosts[0].date).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button className="w-16 h-16 bg-[#00FFD1] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <ArrowUpRight className="w-6 h-6 text-black" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Post Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {blogPosts.slice(1).map((post) => (
              <article 
                key={post.id} 
                className="p-8 bg-white/[0.02] border border-white/10 hover:border-[#00FFD1]/30 transition-all duration-400 group cursor-pointer"
              >
                <span className={`inline-block px-3 py-1 text-xs font-medium mb-4 ${categoryColors[post.category]}`}>
                  {post.category}
                </span>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#00FFD1] transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="text-white/60 text-sm mb-6 leading-relaxed line-clamp-2">
                  {post.summary}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-white/70 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                    <span>{new Date(post.date).toLocaleDateString()}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#00FFD1] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
              {t('blog.stayUpdated')}
            </h2>
            <p className="text-lg text-white/60 mb-8">
              {t('blog.stayUpdatedDesc')}
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder={t('blog.emailPlaceholder')}
                className="flex-1 px-6 py-4 bg-white/5 border border-white/10 text-white placeholder:text-white/60 focus:outline-none focus:border-[#00FFD1]/50"
              />
              <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 h-auto">
                {t('blog.subscribe')}
              </Button>
            </form>
            <p className="text-white/60 text-sm mt-4">
              {t('blog.noSpam')}
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
